import type { App } from '../app/App';
import { rollDie } from '../game/combat/dice';
import { itemDef } from '../game/data/presets';
import { BOARD, MAX_ROLLS, refreshBoard, takeTurn, type BoardReward, type BoardTurn, type SquareKind } from '../game/systems/board';
import { dayKey } from '../game/systems/daily';
import { t } from '../i18n';
import { button, modal, thumb } from './components';
import { h } from './dom';
import { heroArtId, heroDrawing } from '../game/systems/hero';

/** The dice board in a dialog: 20 squares round a 7×5 page, the die in the middle. */

const SQUARE: Record<SquareKind, { icon: string; label: string }> = {
  start: { icon: '🏁', label: 'Start' },
  coins: { icon: '🪙', label: 'Coins' },
  bigcoins: { icon: '💰', label: 'Coin bag' },
  exp: { icon: '⭐', label: 'EXP' },
  chest: { icon: '🎁', label: 'Chest' },
  monster: { icon: '👾', label: 'Monster' },
  boss: { icon: '👹', label: 'Boss' },
  forward: { icon: '⏩', label: '+3' },
  back: { icon: '⏪', label: '−2' },
  extra: { icon: '🎲', label: 'Roll again' },
  mystery: { icon: '❓', label: 'Mystery' },
};
const FACES = ['⚀', '⚁', '⚂', '⚃', '⚄', '⚅'];

/** Grid cell (row, column; 1-based) of square i, clockwise from the top-left corner. */
function cell(i: number): [number, number] {
  if (i <= 6) return [1, i + 1];
  if (i <= 9) return [i - 5, 7];
  if (i <= 16) return [5, 7 - (i - 10)];
  return [5 - (i - 16), 1];
}

function rewardText(r: BoardReward): string {
  switch (r.kind) {
    case 'coins':
      return t('+{n} coins!', { n: r.coins });
    case 'exp':
      return r.levelsGained > 0 ? t('+{n} EXP — LEVEL UP!', { n: r.exp }) : t('+{n} EXP!', { n: r.exp });
    case 'item': {
      const def = itemDef(r.item);
      return t('Found: {item}!', { item: def ? t(def.name) : r.item });
    }
    case 'extra':
      return t('Roll again! +1 roll.');
    case 'fight':
      return r.boss ? t('A board boss blocks the way!') : t('A wild monster jumps out!');
    case 'move':
      return r.by > 0 ? t('Shortcut! Forward {n}.', { n: r.by }) : t('Oops! Back {n}.', { n: -r.by });
    default:
      return '';
  }
}

export async function openDiceBoard(app: App): Promise<void> {
  const hero = app.store.hero;
  if (!hero) {
    app.toast(t('Draw a hero first — monsters need someone to fight!'), 'info');
    return;
  }
  app.store.update(['inventory'], (s) => refreshBoard(s, dayKey()));

  const squares = BOARD.map((kind, i) => {
    const [r, c] = cell(i);
    const info = SQUARE[kind];
    return h('div', { class: `board-sq sq-${kind}`, style: { gridRow: String(r), gridColumn: String(c) }, title: t(info.label) }, h('span', { class: 'sq-icon', 'aria-hidden': 'true' }, info.icon), h('small', null, t(info.label)));
  });
  const token = h('div', { class: 'board-token', 'aria-hidden': 'true' }, thumb(app, heroDrawing(hero), heroArtId(hero), 26));
  const die = h('div', { class: 'board-die', 'aria-hidden': 'true' }, '🎲');
  const rollsLeft = h('p', { class: 'board-rolls' });
  const result = h('p', { class: 'board-result', 'aria-live': 'polite' }, t('Roll the die to move round the board!'));
  const fightSlot = h('div', { class: 'board-fight' });
  let busy = false;
  let closeModal: (v: string) => void = () => {};

  const placeToken = (pos: number): void => squares[pos]!.append(token);
  const showRolls = (): void => {
    const n = app.store.state.board.rolls;
    rollsLeft.textContent = t('Rolls left: {n}/{max}', { n, max: MAX_ROLLS });
    roll.disabled = busy || n <= 0;
  };
  const roll: HTMLButtonElement = button(t('Roll!'), {
    variant: 'primary',
    color: 'green',
    sfx: app,
    onClick: () => void play(),
  });

  async function play(): Promise<void> {
    if (busy || app.store.state.board.rolls <= 0) return;
    busy = true;
    fightSlot.replaceChildren();
    showRolls();
    const face = rollDie();
    for (let i = 0; i < 8; i++) {
      die.textContent = FACES[Math.floor(Math.random() * 6)]!;
      app.sfx.play('click');
      await wait(60);
    }
    die.textContent = FACES[face - 1]!;
    let turn: BoardTurn | null = null;
    app.store.update(['hero', 'inventory', 'quests', 'memories'], (s) => {
      const hh = s.heroes.find((x) => x.id === hero!.id);
      if (hh) turn = takeTurn(s, hh, face);
    });
    if (!turn) {
      busy = false;
      showRolls();
      return;
    }
    const tr: BoardTurn = turn;
    for (const pos of tr.path) {
      placeToken(pos);
      app.sfx.play('flip');
      await wait(170);
    }
    const lines = [rewardText(tr.reward), tr.then ? rewardText(tr.then) : '', tr.lap ? t('Round the board! +{n} coins.', { n: 100 }) : ''].filter(Boolean);
    result.textContent = `🎲 ${face} — ${lines.join(' ')}`;
    const fight = tr.then?.kind === 'fight' ? tr.then : tr.reward.kind === 'fight' ? tr.reward : null;
    app.sfx.play(fight ? 'hit' : tr.reward.kind === 'item' || tr.lap ? 'win' : 'pop');
    if (fight) {
      fightSlot.replaceChildren(
        button(fight.boss ? t('Fight the boss!') : t('Fight!'), {
          variant: 'primary',
          color: 'pink',
          sfx: app,
          onClick: () => {
            closeModal('fight');
            void app.scenes.go('battle', { mode: 'pve', monsterId: fight.monsterId }, 'page');
          },
        }),
        h('small', { class: 'hand-hint' }, t('Or keep rolling: the monster waits on its square.')),
      );
    }
    busy = false;
    showRolls();
  }

  const board = h(
    'div',
    { class: 'dice-board' },
    squares,
    h(
      'div',
      { class: 'board-center' },
      die,
      roll,
      rollsLeft,
      result,
      fightSlot,
      h('p', { class: 'hand-hint' }, t('{n} free rolls every day, plus one for every battle you win.', { n: 5 })),
    ),
  );
  placeToken(app.store.state.board.pos);
  showRolls();
  await modal(t('Dice board'), board, [{ label: t('Close'), value: 'close', variant: 'ghost' }], { className: 'board-card', onOpen: (close) => (closeModal = close) });
}

const wait = (ms: number): Promise<void> => new Promise((r) => setTimeout(r, ms));
